import axios from 'axios';

// Use the correct API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:5001`;

let authToken = '';

const setToken = (token) => {
  authToken = token;
  console.log('🔑 Token set in wallet service');
};

// Get wallet data from the correct endpoint
const getWalletData = async () => {
  try {
    if (!authToken) {
      console.warn('⚠️ No auth token available');
      throw new Error('Authentication token is missing');
    }
    
    // Use the correct endpoint: /api/wallet/balances
    const url = `${API_BASE_URL}/api/wallet/balances`;
    console.log('💰 Fetching wallet data from:', url);
    
    const config = {
      headers: {
        'Authorization': `Bearer ${authToken}`
      },
      timeout: 10000
    };
    
    const response = await axios.get(url, config);
    
    console.log('✅ Wallet data response received');
    
    const data = response.data;
    
    if (data.success === false) {
      throw new Error(data.error || 'Failed to fetch wallet data');
    }
    

    const balances = {
      USD: parseFloat(data.balances?.USD || 0),
      INR: parseFloat(data.balances?.INR || 0),
      LRD: parseFloat(data.balances?.LRD || 0),
      LOCAL: parseFloat(data.balances?.LOCAL || 0),
      BONUS: parseFloat(data.balances?.BONUS || 0),
      LOYALTY: parseFloat(data.balances?.LOYALTY || 0),
      practice: parseFloat(data.balances?.practice || 0)
    };
    
    // Extract country info
    const countryInfo = data.country || {
      code: 'US',
      name: 'United States',
      currencyCode: 'USD',
      currencySymbol: '$',
      localCurrencyStorage: 'LOCAL'
    };
    
    // For Benin, we want to display CFA instead of LOCAL
    if (countryInfo.code === 'BJ' && countryInfo.localCurrencyStorage === 'CFA') {
      // Benin: Use CFA display
      console.log('🌍 User is from Benin, using CFA currency');
    } else if (countryInfo.code === 'LR') {
      // Liberia: Use LRD display
      console.log('🌍 User is from Liberia, using LRD currency');
    }
    
    return {
      success: true,
      balances,
      country: countryInfo,
      primaryBalance: balances.USD || 0,
      primaryCurrency: 'USD',
      currencySymbol: '$',
      countryCode: countryInfo.code,
      countryName: countryInfo.name,
      localCurrencyCode: countryInfo.currencyCode,
      localCurrencySymbol: countryInfo.currencySymbol,
      rawData: data
    };
    
  } catch (error) {
    console.error('❌ Error in getWalletData:', {
      message: error.message,
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data
    });
    
    // Return default structure on error
    return {
      success: false,
      error: error.message,
      balances: {
        USD: 0,
        INR: 0,
        LRD: 0,
        LOCAL: 0,
        practice: 0
      },
      country: {
        code: 'US',
        name: 'United States',
        currencyCode: 'USD',
        currencySymbol: '$',
        localCurrencyStorage: 'LOCAL'
      }
    };
  }
};

// Format currency with symbol
const formatCurrency = (amount, currencyCode = 'USD', symbol = null) => {
  const amountNum = parseFloat(amount) || 0;
  
  // Use provided symbol or get default
  let displaySymbol = symbol;
  if (!displaySymbol) {
    switch (currencyCode.toUpperCase()) {
      case 'USD':
        displaySymbol = '$';
        break;
      case 'INR':
        displaySymbol = '₹';
        break;
      case 'LRD':
        displaySymbol = 'L$';
        break;
      case 'CFA':
      case 'XOF':
        displaySymbol = 'CFA';
        break;
      default:
        displaySymbol = '';
    }
  }
  
  // Format the number based on currency
  let formattedAmount;
  
  switch (currencyCode.toUpperCase()) {
    case 'USD':
      formattedAmount = amountNum.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
      break;
    
    case 'INR':
      formattedAmount = amountNum.toLocaleString('en-IN', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
      break;
    
    case 'LRD':
    case 'CFA':
    case 'XOF':
      formattedAmount = amountNum.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
      break;
    
    default:
      formattedAmount = amountNum.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
  }
  
  // Combine symbol and amount
  if (currencyCode.toUpperCase() === 'CFA' || currencyCode.toUpperCase() === 'XOF') {
    return `${formattedAmount} ${displaySymbol}`;
  } else if (displaySymbol) {
    return `${displaySymbol}${formattedAmount}`;
  } else {
    return formattedAmount;
  }
};

// Get default symbol for currency code
const getDefaultSymbol = (currencyCode) => {
  const symbols = {
    'USD': '$',
    'INR': '₹',
    'LRD': 'L$',
    'XOF': 'CFA',
    'CFA': 'CFA',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥'
  };
  
  return symbols[currencyCode.toUpperCase()] || currencyCode;
};

// Add funds
const addFunds = async (amount, currency, paymentMethod) => {
  try {
    console.log(`➕ Service: Adding ${amount} ${currency}`);
    
    const response = await axios.post(
      `${API_BASE_URL}/api/wallet/add-funds`, 
      {
        amount,
        currency,
        description: `Funds added via ${paymentMethod}`
      }, 
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('✅ Service: Add funds response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Service: Error adding funds:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.message);
  }
};

// Deduct funds
const deductFunds = async (amount, currency, reason) => {
  try {
    console.log(`➖ Service: Deducting ${amount} ${currency} for ${reason}`);
    
    const response = await axios.post(
      `${API_BASE_URL}/api/wallet/deduct`, 
      {
        amount,
        currency,
        description: reason || 'Funds deducted'
      }, 
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('✅ Service: Deduct funds response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Service: Error deducting funds:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.message);
  }
};

// Get transaction history
const getTransactionHistory = async (page = 1, limit = 10) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/wallet/transactions`, {
      headers: {
        'Authorization': `Bearer ${authToken}`
      },
      params: { 
        page, 
        limit,
        offset: (page - 1) * limit 
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('❌ Service: Error fetching transactions:', error);
    throw error;
  }
};

const walletService = {
  setToken,
  getWalletData,
  formatCurrency,
  getDefaultSymbol,
  addFunds,
  deductFunds,
  getTransactionHistory
};

export default walletService;